import clsx from 'clsx'
import { useRef } from 'react'
import { Link } from 'react-router-dom'
import { ChatHistory as ChatHistoryIcon } from '@/assets/icons'
import { LoadingSkeleton } from '@/components/LoadingSkeleton/LoadingSkeleton'
import { useLabelFadeIn } from './useLabelFadeIn'

export type ChatHistoryItemProps = {
  /** The row label */
  label?: string
  /** Icon component — defaults to the chat-history bubble icon */
  icon?: React.FC<React.SVGProps<SVGSVGElement>>
  /** The label is a placeholder that will be replaced (e.g. a chat's first message until its title is generated). Its replacement fades in. */
  labelPending?: boolean
  /** Route to navigate to. Omit to render an action row (a button, e.g. "New Chat") instead of a link. */
  to?: string
  /** Whether this row is the current page: the open chat for a Link row, or the chat an action row leads to */
  isActive?: boolean
  /** A response completed while this chat was not open. */
  unread?: boolean
  /** Disables an action row (button rows only) */
  disabled?: boolean
  /** Click handler — the row's action for a button row, or an extra hook for a Link row (e.g. to close a mobile drawer) */
  onClick?: () => void
  /**
   * Optional trailing slot, e.g. an overflow menu trigger. Revealed on row hover or when any
   * element in the row has focus (via the row's own `group` class and `group-focus-within`) — the
   * trigger itself owns that reveal styling (`AgentV2ConversationMenu` implements it via
   * `showTriggerOnHover`), so this component doesn't need to know
   * anything about menus, hover/focus state, or open state to host one — it only wires the
   * ArrowRight shortcut that moves focus into it from the row's link.
   */
  trailingAction?: React.ReactNode
  /** Test id, e.g. for the New Chat action row or a specific chat row */
  'data-testid'?: string
  /** Shows a shimmer placeholder shaped like this row instead of real content */
  loading?: boolean
}

// useChatHistoryVirtualizer measures New Chat's shared shell to size the virtual chat rows.
const kRowShellClassName = 'group flex w-full items-center gap-2 rounded-md pl-2 pr-1 py-1 h-9 min-h-9 overflow-hidden text-left'
const kRowFocusRingClassName = 'focus:outline-hidden focus-visible:outline-hidden focus-visible:shadow-[0px_0px_0px_2px_rgba(42,73,255)_inset]'
const kRowClassName = clsx(kRowShellClassName, kRowFocusRingClassName)

/**
 * A single row in the side nav's Chat History section — either a chat (a Link, `to` provided) or
 * an action like "New Chat" (a button, no `to`). Both share the same 2.25rem shell, 1rem icon, and
 * single-line fade-truncated label (matches ChatListSidebar's `.chat-list-item-text-fade`, defined
 * in `src/css/text-fade.css`).
 *
 * A chat row's active state uses neutral-950 text/icon at medium weight on a neutral-100
 * background — not the brand-500 blue SideNavItem/SideNavSubItem use — per Figma node 2197:20529.
 * An action row is always brand-500 blue with a brand-100 hover, per Figma node 2197:20496.
 */
export function ChatHistoryItem({
  label,
  labelPending = false,
  icon: Icon = ChatHistoryIcon,
  to,
  isActive = false,
  unread = false,
  disabled = false,
  onClick,
  trailingAction,
  'data-testid': dataTestId,
  loading = false,
}: ChatHistoryItemProps) {
  const rowRef = useRef<HTMLDivElement>(null)
  const { fadingIn, onFadeInEnd } = useLabelFadeIn(label, labelPending)

  // ArrowRight from the row's own link moves focus into its trailing action (the overflow-menu
  // trigger) — Tab still reaches it too; this is a shortcut, not a replacement.
  const handleRowKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== 'ArrowRight') return
    const trigger = rowRef.current?.querySelector('button')
    if (trigger) {
      event.preventDefault()
      trigger.focus()
    }
  }

  if (loading) {
    return (
      <div className={clsx(kRowClassName, 'pointer-events-none')} aria-busy="true" aria-label="Loading chat">
        <LoadingSkeleton width={16} lineHeight={16} />
        <LoadingSkeleton width="70%" lineHeight="1rem" />
      </div>
    )
  }

  const iconAndLabel = (
    <>
      <Icon
        className={clsx('h-4 w-4 shrink-0', to && (isActive ? 'text-text-primary' : 'text-text-tertiary group-hover:text-brand-neutral-700'))}
        aria-hidden="true"
      />

      <span
        className={clsx(
          'flex-1 overflow-hidden whitespace-nowrap text-xs chat-list-item-text-fade',
          !to && 'font-medium',
          fadingIn && 'animate-fadeIn motion-reduce:animate-none'
        )}
        onAnimationEnd={onFadeInEnd}
      >
        {label}
      </span>
    </>
  )

  const trailingActionSlot = trailingAction && <div className="shrink-0">{trailingAction}</div>

  if (!to) {
    return (
      <button
        type="button"
        data-testid={dataTestId}
        disabled={disabled}
        onClick={onClick}
        aria-current={isActive ? 'page' : undefined}
        className={clsx(
          kRowClassName,
          'text-brand-500 hover:bg-surface-brand-hover-strong active:bg-surface-brand-hover-strong disabled:pointer-events-none disabled:opacity-50',
          isActive && 'bg-surface-brand-selected'
        )}
      >
        {iconAndLabel}
        {trailingActionSlot}
      </button>
    )
  }

  // `trailingAction` (an overflow-menu trigger button) must render as a sibling of the `<Link>`,
  // not nested inside it — an `<a>` can't contain interactive content per the HTML5 content model.
  return (
    <div
      ref={rowRef}
      data-testid={dataTestId}
      className={clsx(
        kRowShellClassName,
        'has-[:focus-visible]:shadow-[0px_0px_0px_2px_rgba(42,73,255)_inset]',
        isActive ? 'bg-surface-selected-subtle text-text-primary font-medium' : 'text-text-secondary hover:bg-surface-hover active:bg-surface-hover'
      )}
    >
      <Link
        to={to}
        onClick={onClick}
        onKeyDown={handleRowKeyDown}
        title={label}
        className="flex flex-1 self-stretch items-center gap-2 min-w-0 overflow-hidden rounded-md focus:outline-hidden"
        aria-current={isActive ? 'page' : undefined}
      >
        {iconAndLabel}
        {unread && <span className="sr-only">Unread response</span>}
      </Link>
      {trailingActionSlot}
    </div>
  )
}
